import { getOpenBlockers } from '@/lib/issue-links.js';
import { useParams, Link } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import { useProject } from '@/hooks/useProjects.js';
import { useAllIssues } from '@/hooks/useIssues.js';
import { useProjectStatuses, useAssignableUsers, usePriorities } from '@/hooks/useMeta.js';
import { IssueList } from '@/features/issues/IssueList.js';
import { IssueFilters, buildJql } from '@/components/IssueFilters.js';
import { Pagination } from '@/components/Pagination.js';
import { useUrlFilterState, useUrlTextFilter, useUrlPage } from '@/hooks/useUrlFilterState.js';
import { SearchInput } from '@/components/SearchInput.js';
import { PageHeader } from '@/components/PageHeader.js';
import { LoadingState, ErrorState } from '@/components/PageState.js';

const PAGE_SIZE = 50;

export function ProjectDetail() {
  const { key } = useParams<{ key: string }>();
  const { data: project, isLoading: projectLoading } = useProject(key || '');
  const { filters, setFilters } = useUrlFilterState('updated', 'DESC');
  const [page, setPage] = useUrlPage();
  const [searchText, setSearchText] = useUrlTextFilter('q');

  // Fetch ALL statuses and assignees for this project from Jira API
  const { data: allStatuses } = useProjectStatuses(key || '');
  const { data: allUsers } = useAssignableUsers(key || '');
  const { data: allPriorities } = usePriorities();

  const statuses = useMemo(() => allStatuses || [], [allStatuses]);
  const assignees = useMemo(() => (allUsers || []).map((u) => u.displayName), [allUsers]);

  // Fetch ALL issues for this project (JQL handles status/assignee/sort server-side)
  const jql = useMemo(() => {
    if (!key) return '';
    return buildJql(`project = ${key}`, filters);
  }, [key, filters]);

  const { data: allIssues, isLoading: issuesLoading } = useAllIssues(jql);

  // Client-side: filter by search text, then paginate
  const filtered = useMemo(() => {
    if (!allIssues) return [];
    const candidates = filters.blockedOnly ? allIssues.filter((issue) => getOpenBlockers(issue.links).length > 0) : allIssues;
    if (!searchText.trim()) return candidates;
    const lower = searchText.toLowerCase();
    return candidates.filter(
      (issue) =>
        issue.summary.toLowerCase().includes(lower) ||
        issue.key.toLowerCase().includes(lower),
    );
  }, [allIssues, searchText, filters.blockedOnly]);

  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));

  // Clamp page to valid range when search changes
  const safePage = Math.min(page, totalPages);

  const pagedIssues = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);

  // Reset to page 1 when search text changes
  const handleSearch = useCallback((text: string) => {
    setSearchText(text);
    setPage(1);
  }, [setSearchText, setPage]);

  const handleSort = useCallback((field: string) => {
    setFilters((prev) => ({
      ...prev,
      sortField: field,
      sortDir: prev.sortField === field && prev.sortDir === 'DESC' ? 'ASC' : 'DESC',
    }));
    setPage(1);
  }, [setFilters, setPage]);

  const handleFilterChange = useCallback((newFilters: typeof filters) => {
    setFilters(newFilters);
    setPage(1);
  }, [setFilters, setPage]);

  if (projectLoading) return <LoadingState label="Loading project..." />;
  if (!project) return <ErrorState message="Project not found" />;

  const resultCount = allIssues
    ? ((searchText.trim() || filters.blockedOnly) ? `${totalFiltered} of ${allIssues.length} issues` : `${allIssues.length} issues`)
    : undefined;

  return (
    <div>
      <PageHeader
        breadcrumb={<Link to="/projects" className="hover:text-blue-700 hover:underline">Projects</Link>}
        icon={project.avatarUrl ? <img src={project.avatarUrl} alt="" className="h-9 w-9 rounded-md" /> : undefined}
        title={project.name}
        meta={resultCount}
        actions={<SearchInput value={searchText} onChange={handleSearch} placeholder="Filter by summary or key..." className="w-full sm:w-72" />}
      />

      <IssueFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        statuses={statuses}
        assignees={assignees}
      />

      {issuesLoading ? (
        <LoadingState label="Loading issues..." />
      ) : (
        <>
          <IssueList
            issues={pagedIssues}
            sortField={filters.sortField}
            sortDir={filters.sortDir}
            onSort={handleSort}
            editable
            priorities={allPriorities}
            users={allUsers}
          />
          {totalFiltered > PAGE_SIZE && (
            <div className="mt-4">
              <Pagination
                page={safePage}
                pageSize={PAGE_SIZE}
                total={totalFiltered}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
